// Local folder storage via the File System Access API (Chrome/Edge desktop only).
// The directory handle is persisted in IndexedDB so the user re-grants access with
// a single click per session. Not available on Safari/Firefox or any mobile browser.

const DB_NAME = "vxf_fs";
const STORE = "handles";
const KEY = "gallery_dir";

function idb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(k) {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const r = db.transaction(STORE, "readonly").objectStore(STORE).get(k);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

async function idbSet(k, v) {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(v, k);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export function isFsSupported() {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

export async function connectFolder() {
  const handle = await window.showDirectoryPicker({ mode: "readwrite" });
  await idbSet(KEY, handle);
  return handle;
}

export async function getSavedFolder() {
  try {
    return (await idbGet(KEY)) || null;
  } catch {
    return null;
  }
}

export async function forgetFolder() {
  try { await idbSet(KEY, null); } catch { /* ignore */ void 0; }
}

async function verifyPermission(handle, write = true) {
  const opts = { mode: write ? "readwrite" : "read" };
  if ((await handle.queryPermission(opts)) === "granted") return true;
  if ((await handle.requestPermission(opts)) === "granted") return true;
  return false;
}

// Returns a usable (permission-granted) handle, or null. Pass prompt=false to avoid
// triggering a permission request outside a user gesture.
export async function ensureFolder(prompt = false) {
  const handle = await getSavedFolder();
  if (!handle) return null;
  if (prompt) {
    if (!(await verifyPermission(handle, true))) return null;
    return handle;
  }
  const opts = { mode: "readwrite" };
  try {
    if ((await handle.queryPermission(opts)) === "granted") return handle;
  } catch { /* ignore */ void 0; }
  return null;
}

async function subDir(root, name) {
  return await root.getDirectoryHandle(name, { create: true });
}

// Saves a blob directly into the folder the user picked and returns the file name.
export async function saveToFolder(root, { filename, blob }) {
  const fh = await root.getFileHandle(filename, { create: true });
  const writable = await fh.createWritable();
  await writable.write(blob);
  await writable.close();
  return `${root.name}/${filename}`;
}

export function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(",");
  const mime = (header.match(/data:(.*?);base64/) || [])[1] || "image/png";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
